import * as React from 'react'

import { action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { DeliveryStatus } from '~/client/graph'
import DesktopCalendarViewStore from '~/client/src/desktop/components/DesktopCalendar/DesktopCalendar.store'
import DesktopCalendarColumn from '~/client/src/desktop/components/DesktopCalendar/DesktopCalendarColumn'
import CalendarEventComponent from '~/client/src/shared/components/CalendarDayView/components/CalendarEvent/CalendarEvent'
import HourSpace from '~/client/src/shared/components/CalendarDayView/components/HourSpace'
import IEditableCalendarEvent from '~/client/src/shared/components/CalendarDayView/components/IEditableCalendarEvent'
import CalendarTimeline from '~/client/src/shared/components/CalendarTimeline/CalendarTimeline'
import CalendarEvent from '~/client/src/shared/models/CalendarEvent'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

// localization: no display text to translate

interface IProps {
  calendarColumn: DesktopCalendarColumn
  store: DesktopCalendarViewStore
  onEventClicked?: (event: CalendarEvent) => void
  initialDate?: Date
  isEventCreatingDisabled?: boolean
  isDayCalendarModeActive?: boolean
  isToday?: boolean
  shouldRenderDot?: boolean
  onEventMoved?: (e: CalendarEvent, x: number, y: number) => void
  onEventTimeChange?: (
    e: CalendarEvent,
    startDate: number,
    endDate: number,
  ) => void
  onLongSelect?: (event: CalendarEvent) => void
  selectedEvent?: IEditableCalendarEvent
  projectDateStore?: ProjectDateStore
}

@inject('projectDateStore')
@observer
export default class DesktopCalendarColumnComponent extends React.Component<IProps> {
  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { isDayCalendarModeActive, isToday, shouldRenderDot } = this.props

    return (
      <div className="relative x-axis-column-width">
        {isToday && <CalendarTimeline shouldRenderDot={shouldRenderDot} />}
        <div
          className={classList({
            'bl-light-grey relative': true,
            'bt-light-grey': isDayCalendarModeActive,
          })}
        >
          {this.renderHourSpaces()}
          {this.renderDayEvents()}
        </div>
      </div>
    )
  }

  private renderHourSpaces() {
    const { store, initialDate } = this.props

    return store.dayHours.map(hour => {
      return (
        hour < 24 && (
          <HourSpace
            key={hour}
            hour={hour}
            timeInterval={store.timeInterval}
            rowsNumber={store.rowsNumber}
            onEmptyCellTouchStart={this.onHourSpaceTouchStart}
            shouldHandleMouseClick={true}
            activeDate={initialDate}
          />
        )
      )
    })
  }

  private renderDayEvents() {
    const {
      store,
      calendarColumn,
      onEventMoved,
      onEventTimeChange,
      onEventClicked,
      onLongSelect,
      selectedEvent,
      projectDateStore: { isSameDay },
    } = this.props
    const columnEvents = store
      .getEventsByColumn(calendarColumn)
      .filter(
        event =>
          isSameDay(event.startDate, event.endDate) &&
          (!event.data || event.data?.status !== DeliveryStatus.Deleted),
      )
    store.restorePositionedEvents()
    return columnEvents.map((event, idx) => {
      const { style, className } = store.getEventSizeAndPosition(
        event,
        columnEvents,
        calendarColumn.date,
      )

      return (
        <CalendarEventComponent
          key={idx}
          event={event}
          onEventClicked={onEventClicked}
          style={style}
          className={className}
          onLongSelect={onLongSelect}
          onEventMoved={onEventMoved}
          onEventTimeChange={onEventTimeChange}
          isSelected={selectedEvent?.event?.dataId === event.dataId}
        />
      )
    })
  }

  @action.bound
  private onHourSpaceTouchStart(date: Date) {
    const { calendarColumn, isEventCreatingDisabled, store } = this.props
    if (!isEventCreatingDisabled) {
      store.activateNewEventMode(calendarColumn, date)
    }
  }
}
